import { horizontalListSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import { css } from '@emotion/react';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { CALENDAR_Z_INDICES } from '../../constants';
import { useCalendarGridStore } from '../../store/calendar-grid.store';
import { CalendarColumnGroup, CalendarColumnHeaderType } from '../../types';
import { ColumnHeader } from './column-header';
import { SortableColumnHeader } from './sortable-column-header';

interface Props {
  groupInfo: CalendarColumnGroup;
  gridColumn: string;
  isGroupHeaderVisible: boolean;
  headerType: CalendarColumnHeaderType;
  columnIds: string[];
}

export const GroupHeader = ({ groupInfo, gridColumn, isGroupHeaderVisible, headerType, columnIds }: Props) => {
  const isColumnReorderEnabled = useCalendarGridStore((s) => s.isColumnReorderEnabled);

  const groupNameSpan = isGroupHeaderVisible && (
    <div css={groupNameContainerStyle} style={{ gridColumn: `1 / span ${groupInfo.columnIds.length}` }}>
      <Text as='span' color='subdued'>
        {groupInfo.name}
      </Text>
    </div>
  );

  if (!isColumnReorderEnabled) {
    return (
      <header data-group-header css={containerStyle} style={{ gridColumn }}>
        {groupNameSpan}
        {columnIds.map((columnId) => (
          <ColumnHeader key={columnId} columnId={columnId} headerType={headerType} />
        ))}
      </header>
    );
  }

  return (
    <SortableContext items={columnIds} strategy={horizontalListSortingStrategy}>
      <header data-group-header css={containerStyle} style={{ gridColumn }}>
        {groupNameSpan}
        {columnIds.map((columnId) => (
          <SortableColumnHeader key={columnId} columnId={columnId} headerType={headerType} />
        ))}
      </header>
    </SortableContext>
  );
};

const containerStyle = css({
  display: 'grid',
  gridTemplateColumns: 'subgrid',
  gridRow: 1,
  position: 'sticky',
  top: 0,
  zIndex: CALENDAR_Z_INDICES.groupHeader,
  boxShadow: theme.shadows.light,
  backgroundColor: theme.colors.white,
});

const groupNameContainerStyle = css({
  display: 'flex',
  justifyContent: 'center',
  borderBottom: `1px solid ${theme.colors.neutral20}`,
  '> span': {
    minWidth: 300,
    backgroundColor: theme.colors.neutral10,
    width: '30vw',
    textAlign: 'center',
    position: 'sticky',
    left: 'var(--time-column-width)',
    right: 0,
  },
});
